import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { Grid } from '@mui/material';
import { AssignRolePlaceholder } from 'src/components';
import type { AccountsRolesAutocompleteValue } from 'src/components/AccountsRolesAutoComplete';
import { AccountsRolesAutoComplete } from 'src/components/AccountsRolesAutoComplete';
import type { Apollo } from 'src/gql/generated';
import type { UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

export const assignRoleRowHeightPx = 72;

const useStyles = makeStyles()(theme => ({
  item: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
  },
  row: {
    '&:first-child': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    borderBottom: `1px solid ${theme.palette.divider}`,
    height: assignRoleRowHeightPx,
    padding: theme.spacing(1),
  },
}));

/**
 * If it's a user outside the org, we only have email.
 * If it's a user within the org, we have userId.
 */
export interface AssignRoleDetails {
  email?: string;
  permissionRole?: Apollo.PermissionRole;
  userId?: UUID;
}

/**  UUID is UserDefinedRole.id. */
export type RoleUserTuple = [UUID, AssignRoleDetails | null];
export interface AssignmentWithRole {
  __typename: 'AssigneeRolePair';
  assignee: UserSubset | null;
  assigneeRole: Pick<Apollo.UserDefinedRole, '__typename' | 'id' | 'roleName'>;
}

export interface AssignRoleRowProps {
  assignment: AssignmentWithRole;
  initialUserOrEmail: AccountsRolesAutocompleteValue;
  onChange: (tuple: RoleUserTuple) => void;
}

export function AssignRoleRow(props: AssignRoleRowProps): JSX.Element {
  const { assignment } = props;
  const { classes } = useStyles();
  const { handleAutocompleteChange, userOrSuggested } = useAssignRoleRow(props);

  return (
    <Grid container className={classes.row} sx={{ alignItems: 'center' }}>
      <Grid className={classes.item} size={6}>
        <AssignRolePlaceholder userDefinedRole={assignment.assigneeRole} />
      </Grid>
      <Grid className={classes.item} size={6}>
        <AccountsRolesAutoComplete
          suggestInviteEmail
          includeOptions="users"
          value={userOrSuggested}
          variant="avatar"
          onChange={handleAutocompleteChange}
        />
      </Grid>
    </Grid>
  );
}

export function useAssignRoleRow({ assignment, initialUserOrEmail, onChange }: AssignRoleRowProps) {
  const [userOrSuggested, setUserOrSuggested] =
    useState<AccountsRolesAutocompleteValue>(initialUserOrEmail);

  function handleChangeRowInfo(userOrSuggested: AccountsRolesAutocompleteValue): void {
    const roleId = assignment.assigneeRole.id;

    if (!userOrSuggested) return onChange([roleId, null]);

    // Should be impossible b/c of includeOptions="users" in Autocomplete...
    if (userOrSuggested.__typename !== 'User') return;

    if ('id' in userOrSuggested)
      return onChange([
        roleId,
        {
          userId: userOrSuggested.id,
        },
      ]);

    // Handle suggested email.
    return onChange([
      roleId,
      {
        email: userOrSuggested.email.trim().toLowerCase(),
      },
    ]);
  }

  function handleAutocompleteChange(
    _event: React.ChangeEvent<unknown>,
    value: AccountsRolesAutocompleteValue
  ) {
    setUserOrSuggested(value);
    handleChangeRowInfo(value);
  }

  return { handleAutocompleteChange, userOrSuggested };
}
